import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/useAuth';
import { useMainCourante } from '../../../contexts/useMainCourante';
import { useNavigate } from 'react-router-dom';
import { LogOut, Menu, X, Users, MessageSquare, Home } from 'lucide-react';

export default function DashboardHeader() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, authorizedApps } = useAuth();
  const { unreadCount } = useMainCourante();
  const navigate = useNavigate();

  const canAccessMainCourante = authorizedApps.includes('hub_maincourante');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatDate = (date: Date) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const dayName = days[date.getDay()];
    const dayNum = date.getDate();
    const month = months[date.getMonth()];
    return `${dayName} ${dayNum} ${month}`;
  };

  const formatTime = (date: Date) => {
    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-hub-header border-b border-hub-border px-5 pb-2">
      {/* Desktop layout */}
      <div className="hidden lg:flex h-14 items-center pt-6">
        <div className="flex-1 flex items-center">
          <span className="text-hub-text font-bold text-base tracking-widest uppercase select-none">
            HUB DT CENTRE
          </span>
        </div>

        <div className="flex-1 flex items-center justify-end gap-4">
          <div className="text-hub-muted text-sm font-medium whitespace-nowrap">
            <span className="text-hub-text">{formatDate(currentTime)}</span>
            <span className="mx-2 text-hub-border">|</span>
            <span className="text-hub-accent font-bold">{formatTime(currentTime)}</span>
          </div>
          {user && (
            <div className="text-hub-text text-sm font-medium">
              <span className="text-hub-accent font-bold">{user.prenom} {user.nom}</span>
              {user.role && (
                <span className="ml-2 text-hub-muted text-xs">({user.role})</span>
              )}
            </div>
          )}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-hub-card hover:bg-hub-border text-hub-text text-xs font-bold tracking-widest uppercase rounded transition-colors duration-200"
          >
            <Home size={14} />
            Accueil
          </button>
          {canAccessMainCourante && (
            <button
              onClick={() => navigate('/main-courante')}
              className="relative flex items-center gap-2 px-4 py-2 bg-hub-card hover:bg-hub-border text-hub-text text-xs font-bold tracking-widest uppercase rounded transition-colors duration-200"
            >
              <MessageSquare size={14} />
              Main Courante
              {unreadCount > 0 && (
                <>
                  <span className="absolute -top-2 -right-2 inline-flex h-5 w-5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500"></span>
                  </span>
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </>
              )}
            </button>
          )}
          {user?.role === 'superviseur' && (
            <button
              onClick={() => navigate('/admin/users')}
              className="px-4 py-2 bg-hub-blue hover:bg-hub-blue-hover text-white text-xs font-bold tracking-widest uppercase rounded transition-colors duration-200"
            >
              Utilisateurs
            </button>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold tracking-widest uppercase rounded transition-colors duration-200"
          >
            <LogOut size={14} />
            Déconnexion
          </button>
        </div>
      </div>

      {/* Mobile/Tablet layout */}
      <div className="lg:hidden h-14 px-3">
        <div className="flex items-center justify-between h-full">
          {/* Hamburger menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-hub-text hover:text-hub-accent transition-colors"
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Date et heure */}
          <div className="text-xs font-medium text-center">
            <div className="text-hub-text">{formatDate(currentTime)}</div>
            <div className="text-hub-accent font-bold text-sm">{formatTime(currentTime)}</div>
          </div>

          {/* Spacer for symmetry */}
          <div className="w-10"></div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-hub-header border-b border-hub-border shadow-lg">
            <div className="px-4 py-3 space-y-3">
              {/* User info */}
              {user && (
                <div className="text-hub-text text-sm pb-3 border-b border-hub-border">
                  <div className="font-bold text-hub-accent">{user.prenom} {user.nom}</div>
                  {user.role && (
                    <div className="text-hub-muted text-xs mt-1">Rôle: {user.role}</div>
                  )}
                </div>
              )}

              {/* Menu items */}
              <button
                onClick={() => {
                  navigate('/');
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-hub-card hover:bg-hub-border text-hub-text rounded transition-colors duration-200"
              >
                <Home size={18} />
                <span className="font-medium flex-1">Accueil</span>
              </button>
              {canAccessMainCourante && (
                <button
                  onClick={() => {
                    navigate('/main-courante');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-hub-card hover:bg-hub-border text-hub-text rounded transition-colors duration-200"
                >
                  <MessageSquare size={18} />
                  <span className="font-medium flex-1">Main Courante</span>
                  {unreadCount > 0 && (
                    <span className="relative inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
                      <span className="relative">{unreadCount > 99 ? '99+' : unreadCount}</span>
                    </span>
                  )}
                </button>
              )}
              {user?.role === 'superviseur' && (
                <button
                  onClick={() => {
                    navigate('/admin/users');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-hub-blue hover:bg-hub-blue-hover text-white rounded transition-colors duration-200"
                >
                  <Users size={18} />
                  <span className="font-medium">Utilisateurs</span>
                </button>
              )}

              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded transition-colors duration-200"
              >
                <LogOut size={18} />
                <span className="font-medium">Déconnexion</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}